// CSV エクスポート/インポート ユーティリティ

export interface CsvColumn {
  key: string;
  label: string;
}

/**
 * データをCSV形式でエクスポート
 */
export function exportToCsv<T extends Record<string, any>>(
  data: T[],
  columns: CsvColumn[],
  filename: string
): void {
  // BOMを追加（Excelで文字化けしないように）
  const BOM = '\uFEFF';
  
  // ヘッダー行
  const header = columns.map(col => escapeCSV(col.label)).join(',');
  
  // データ行
  const rows = data.map(item => 
    columns.map(col => escapeCSV(formatValue(item[col.key]))).join(',')
  );
  
  const csvContent = BOM + [header, ...rows].join('\n');
  
  // ダウンロード
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = `${filename}_${new Date().toISOString().split('T')[0]}.csv`;
  link.click();
  URL.revokeObjectURL(link.href);
}

/**
 * CSVファイルをパース
 */
export function parseCsv<T>(csvText: string, columns: CsvColumn[]): T[] {
  const lines = csvText.split('\n').filter(line => line.trim());
  if (lines.length < 2) return [];
  
  const headers = parseCSVLine(lines[0]);
  const keyMap: Record<string, string> = {};
  
  // ヘッダーからキーへのマッピング
  headers.forEach((header, index) => {
    const col = columns.find(c => c.label === header.trim());
    if (col) {
      keyMap[index] = col.key;
    }
  });
  
  // データ行をパース
  const data: T[] = [];
  for (let i = 1; i < lines.length; i++) {
    const values = parseCSVLine(lines[i]);
    const row: Record<string, any> = {};
    
    values.forEach((value, index) => {
      const key = keyMap[index];
      if (key) {
        row[key] = value.trim();
      }
    });
    
    if (Object.keys(row).length > 0) {
      data.push(row as T);
    }
  }
  
  return data;
}

/**
 * CSVの値をエスケープ
 */
function escapeCSV(value: string): string {
  if (value.includes(',') || value.includes('"') || value.includes('\n')) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

/**
 * 値をフォーマット
 */
function formatValue(value: any): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'number') return String(value);
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  if (value instanceof Date) return value.toISOString().split('T')[0];
  return String(value);
}

/**
 * CSV行をパース（ダブルクォート対応）
 */
function parseCSVLine(line: string): string[] {
  const result: string[] = [];
  let current = '';
  let inQuotes = false;
  
  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    
    if (inQuotes) {
      if (char === '"') {
        if (line[i + 1] === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        current += char;
      }
    } else {
      if (char === '"') {
        inQuotes = true;
      } else if (char === ',') {
        result.push(current);
        current = '';
      } else {
        current += char;
      }
    }
  }
  
  result.push(current);
  return result;
}

/**
 * CSVインポート用コンポーネントのプロパティ
 */
export interface CsvImportProps {
  onImport: (data: any[]) => void;
  columns: CsvColumn[];
  templateData?: any[];
}

/**
 * テンプレートCSVをダウンロード
 */
export function downloadTemplate(columns: CsvColumn[], filename: string, sampleData?: any[]): void {
  const data = sampleData || [columns.reduce((acc, col) => {
    acc[col.key] = `（${col.label}のサンプル）`;
    return acc;
  }, {} as Record<string, string>)];
  
  exportToCsv(data, columns, `${filename}_template`);
}

